import { exigirDefinicionCoherente } from '../formularios/definicion.js';
/**
 * NMS · New Mobility Score · 0–9.
 *
 * **Es el instrumento original de Parker & Palmer (1993)**, no la adaptación que
 * circulaba en el protocolo. La decisión estaba abierta desde julio
 * (`escalas-fhir-validacion.md` §3.1: alinear al original o congelar la
 * adaptación como instrumento local no comparable) y se resolvió por el
 * original: tres dominios —moverse por la casa, salir de la casa, salir a hacer
 * las compras— con las **mismas cuatro anclas** cada uno, 0 a 3, total 0 a 9.
 *
 * La adaptación del protocolo convertía "ayuda técnica" en una pregunta aparte,
 * eliminaba el dominio de compras y usaba valores {0,1,3}: mismo nombre, mismo
 * rango y un puntaje que no significa lo mismo. Queda retirada.
 *
 * **Cómo se administra** (`dev/fuentes-fundacion/Escalas/nms.svg`): cada dominio
 * se resuelve con hasta tres preguntas de sí/no. Para un adulto mayor recién
 * operado, tres binarias son más fáciles que elegir entre cuatro anclas
 * matizadas. El árbol es presentación: **lo que se registra es el ancla**, que es
 * lo que hace comparable el puntaje.
 *
 * **Skip-logic:** si no camina por su casa, tampoco sale ni hace las compras. Los
 * otros dos dominios no se preguntan y el total es 0. Es la única `enableWhen`
 * de las cuatro escalas.
 */
/**
 * Las cuatro anclas del original, iguales en los tres dominios.
 *
 * `no-puede` cambia de etiqueta según el dominio —"no camino por mi casa" no es
 * "no salgo a hacer las compras"— pero es la misma ancla y vale 0 en los tres.
 */
function anclas(etiquetaNoPuede) {
    return [
        { codigo: 'sin-ayuda', etiqueta: 'Sí, sin ninguna ayuda', puntos: 3 },
        { codigo: 'ayuda-tecnica', etiqueta: 'Sí, con andador, bastón o muletas', puntos: 2 },
        { codigo: 'ayuda-persona', etiqueta: 'Sí, con la ayuda de otra persona', puntos: 1 },
        { codigo: 'no-puede', etiqueta: etiquetaNoPuede, puntos: 0 },
    ];
}
/**
 * El árbol de `nms.svg`, idéntico en los tres dominios.
 *
 * El orden de las dos preguntas de adentro no es intercambiable: se pregunta
 * primero por la ayuda de una persona y después por la ayuda técnica, porque en
 * el original "con ayuda de otra persona" (1) manda sobre "con ayuda técnica"
 * (2) — quien necesita a alguien puntúa 1 aunque además use andador.
 */
function arbolDelDominio(pregunta) {
    return {
        clase: 'pregunta',
        texto: pregunta,
        no: { clase: 'resultado', opcion: 'no-puede' },
        si: {
            clase: 'pregunta',
            texto: '¿Necesitás que alguien te ayude?',
            si: { clase: 'resultado', opcion: 'ayuda-persona' },
            no: {
                clase: 'pregunta',
                texto: '¿Usás alguna ayuda? (andador, muletas)',
                si: { clase: 'resultado', opcion: 'ayuda-tecnica' },
                no: { clase: 'resultado', opcion: 'sin-ayuda' },
            },
        },
    };
}
/** Los tres dominios, en el orden en que se administran. */
export const DOMINIOS_NMS = ['casa', 'salir', 'compras'];
const TEXTO_CASA = '¿Caminás por tu casa?';
const TEXTO_SALIR = '¿Salís de tu casa?';
const TEXTO_COMPRAS = '¿Salís a hacer las compras u otras actividades afuera?';
/** Si no camina por su casa, el resto del instrumento no se pregunta. */
const CAMINA_EN_CASA = [{ pregunta: 'casa', operador: 'distinto', valor: 'no-puede' }];
export const FORMULARIO_NMS = exigirDefinicionCoherente({
    id: 'nms',
    clase: 'escala',
    instrumento: 'nms',
    titulo: 'Movilidad',
    version: '1.0.0',
    estado: 'active',
    preguntas: [
        {
            id: 'casa',
            texto: TEXTO_CASA,
            campo: { tipo: 'opcion', opciones: anclas('No camino por mi casa') },
            presentacion: { modo: 'arbol', arbol: arbolDelDominio(TEXTO_CASA) },
            opcional: false,
        },
        {
            id: 'salir',
            texto: TEXTO_SALIR,
            campo: { tipo: 'opcion', opciones: anclas('No salgo de mi casa') },
            presentacion: { modo: 'arbol', arbol: arbolDelDominio(TEXTO_SALIR) },
            cuando: [...CAMINA_EN_CASA],
            opcional: false,
        },
        {
            id: 'compras',
            texto: TEXTO_COMPRAS,
            campo: { tipo: 'opcion', opciones: anclas('No salgo a hacer las compras ni otras cosas') },
            presentacion: { modo: 'arbol', arbol: arbolDelDominio(TEXTO_COMPRAS) },
            cuando: [...CAMINA_EN_CASA],
            opcional: false,
        },
    ],
});
//# sourceMappingURL=nms.js.map